<template>
  <el-dialog
    :title="title"
    :visible="visible"
    :before-close="beforeClose"
    @open="onOpen"
    @closed="onClosed"
    @update:visible="setVisibility($event)"
  >
    <p v-t="{path: 'components.createLocaleDialog.text', args: {key: key || ''}}" class="mt-0"></p>

    <el-input v-model="form.key" @blur="setKey">
      <span slot="prepend">{{ keyLabel }}</span>
    </el-input>

    <dx-data-grid
      ref="dataGrid"
      :columns="columns"
      :filter-value="filterValue"
      :data-source="store"
      :remote-operations="remoteOperations"
      :allow-column-resizing="true"
      :show-borders="true"
      :show-row-lines="false"
      :show-column-lines="true"
      :row-alternation-enabled="true"
      @init-new-row="onInitNewRow"
      @row-inserting="onRowInserting"
    >
      <dx-sorting mode="none"/>

      <dx-editing
        :allow-adding="true"
        :allow-updating="true"
        mode="batch"
      />
    </dx-data-grid>

    <el-divider/>

    <div class="d-flex justify-content-end">
      <el-button @click="close" v-t="'common.cancel'"/>

      <el-button
        type="primary"
        @click="saveAndClose"
        v-t="'common.apply'"
      />
    </div>
  </el-dialog>
</template>

<script>
import { DxDataGrid } from 'devextreme-vue';
import { DxEditing, DxSorting } from 'devextreme-vue/data-grid';
import { get } from 'lodash';
import createBackendStore from '@/lib/create-backend-store';
import { buildColumns } from '@/lib/data-grid-columns';
import { translateProperty } from '@/lib/util';

export default {
  name: 'CreateLocaleDialog',
  components: {
    DxDataGrid,
    DxEditing,
    DxSorting,
  },
  data() {
    return {
      columns: [],
      store: null,
      remoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      key: '',
      form: {
        key: '',
      },
      presetLanguage: true,
    };
  },
  props: {
    visible: { type: Boolean, default: false },
    value: { type: Object, default: null },
  },
  computed: {
    title() {
      return this.$t('components.createLocaleDialog.title');
    },
    namespace() {
      return get(this.value, 'namespace');
    },
    language() {
      return this.value.language;
    },
    dataGrid() {
      return this.$refs.dataGrid.instance;
    },
    filterValue() {
      return [
        ['namespace', '=', this.namespace],
        ['key', '=', this.key],
      ];
    },
    keyLabel() {
      return translateProperty('locale', 'key');
    },
  },
  methods: {
    buildColumns,
    configureColumns() {
      return this.buildColumns('locale', [
        {
          property: 'namespace',
          visible: false,
        },
        {
          property: 'key',
          visible: false,
          validationRules: null,
        },
        {
          property: 'language',
          width: '15%',
        },
        {
          property: 'translation',
        },
      ]);
    },
    configureStore(columns) {
      return createBackendStore('/locales', {
        fields: columns.map(c => c.dataField),
      });
    },
    setVisibility(value) {
      this.$emit('update:visible', value);
    },
    setKey() {
      this.key = this.form.key;
    },
    async saveAndClose() {
      await this.dataGrid.saveEditData();
      this.close();
    },
    close() {
      this.setVisibility(false);
    },
    addRow() {
      if (this.dataGrid.isReady()) {
        this.dataGrid.addRow();
        return Promise.resolve();
      }

      // delay until content becomes ready
      return new Promise((resolve) => {
        const onContentReady = () => {
          this.dataGrid.off('contentReady', onContentReady);
          this.dataGrid.addRow();
          resolve();
        };

        this.dataGrid.on('contentReady', onContentReady);
      });
    },
    onInitNewRow(event) {
      if (this.presetLanguage) {
        // eslint-disable-next-line no-param-reassign
        event.data.language = this.language;

        this.$nextTick(() => {
          this.dataGrid.editCell(0, 'translation');
        });
      }
    },
    onRowInserting(event) {
      // eslint-disable-next-line no-param-reassign
      event.data.namespace = this.namespace;
      // eslint-disable-next-line no-param-reassign
      event.data.key = this.key;
    },
    onOpen() {
      const { key } = this.value;
      this.key = key;
      this.form.key = key;

      this.$nextTick(async () => {
        this.presetLanguage = !!this.value.language;
        await this.addRow();
        this.presetLanguage = false;
      });
    },
    async beforeClose(done) {
      if (this.dataGrid.hasEditData()) {
        const message = this.$t('components.createLocaleDialog.dataGridHasEditDataMessage');
        this.$message({ message, type: 'warning' });
      } else {
        done();
      }
    },
    onClosed(event) {
      this.dataGrid.cancelEditData();
      this.$emit('closed', event);
    },
  },
  created() {
    this.columns = this.configureColumns();
    this.store = this.configureStore(this.columns);
  },
};
</script>

<style scoped>

</style>
